/**
 * WalletTipJarPoster — printable "tip me" poster block on /wallet.
 *
 * Renders the DJ's public tip URL as a big scannable QR plus their handle,
 * with three CTAs:
 *   • Copy link
 *   • Copy QR PNG (to clipboard as an image — for pasting into Instagram
 *     stories, tweet drafts, Discord etc.)
 *   • Download poster PNG (A4-ratio print-ready image with branding + QR +
 *     handle — DJs stick this on their booth)
 *
 * We refuse to render if Stripe Connect isn't wired up yet — no point
 * printing a QR that leads to "This DJ hasn't set up payouts yet."
 */
import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Download, Image as ImageIcon, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function WalletTipJarPoster({ user }) {
  const svgWrapRef = useRef(null);
  const [busy, setBusy] = useState(null); // "copy-link" | "copy-img" | "download"

  if (!user?.handle) return null;
  const tipUrl = `${window.location.origin}/tip/${user.handle}`;

  const copyLink = async () => {
    setBusy("copy-link");
    try {
      await navigator.clipboard.writeText(tipUrl);
      toast.success("Tip-jar link copied");
    } catch {
      toast.error("Clipboard blocked · long-press the URL to copy");
    } finally { setBusy(null); }
  };

  // Turn the on-page SVG into a PNG via a temporary canvas. Reused by both
  // "Copy QR PNG" (clipboard) and "Download poster PNG" (a4-ratio composite).
  const renderPng = async ({ posterLayout }) => {
    const svgEl = svgWrapRef.current?.querySelector("svg");
    if (!svgEl) throw new Error("QR SVG missing");
    const xml = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);

    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          if (posterLayout) {
            // A4-portrait ratio at print resolution (72dpi × 8.27in / 11.69in)
            canvas.width = 1240; canvas.height = 1754;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#0B0E14";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            // Cyan accent bars top + bottom
            ctx.fillStyle = "#00F0FF";
            ctx.fillRect(0, 0, canvas.width, 12);
            ctx.fillRect(0, canvas.height - 12, canvas.width, 12);
            // Header
            ctx.fillStyle = "#00F0FF";
            ctx.font = "bold 42px system-ui,-apple-system,sans-serif";
            ctx.textAlign = "center";
            ctx.fillText("// NEURALDECK · TIP JAR", canvas.width / 2, 120);
            // Big handle
            ctx.fillStyle = "#FFFFFF";
            ctx.font = "bold 110px system-ui,-apple-system,sans-serif";
            ctx.fillText(`@${user.handle}`, canvas.width / 2, 260);
            // QR (centered, 800px)
            const qrSize = 800;
            ctx.drawImage(img, (canvas.width - qrSize) / 2, 340, qrSize, qrSize);
            // Instructions
            ctx.fillStyle = "#94A3B8";
            ctx.font = "34px system-ui,-apple-system,sans-serif";
            ctx.fillText("Scan to tip · fund the next set", canvas.width / 2, 1240);
            ctx.fillStyle = "#FFFFFF";
            ctx.font = "bold 42px system-ui,-apple-system,sans-serif";
            ctx.fillText(tipUrl.replace(/^https?:\/\//, ""), canvas.width / 2, 1330);
            ctx.fillStyle = "#FF2A6D";
            ctx.font = "24px system-ui,-apple-system,sans-serif";
            ctx.fillText("90 % goes to the DJ · 10 % platform · no signup for tippers", canvas.width / 2, 1450);
            ctx.fillStyle = "#94A3B8";
            ctx.font = "22px monospace";
            ctx.fillText(user.display_name || "", canvas.width / 2, 1600);
          } else {
            // Bare QR PNG for social sharing — 800 × 800 on white so it
            // scans reliably against any bg.
            canvas.width = 800; canvas.height = 800;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 40, 40, 720, 720);
          }
          canvas.toBlob(b => {
            URL.revokeObjectURL(svgUrl);
            b ? resolve(b) : reject(new Error("toBlob returned null"));
          }, "image/png");
        } catch (e) {
          URL.revokeObjectURL(svgUrl);
          reject(e);
        }
      };
      img.onerror = () => { URL.revokeObjectURL(svgUrl); reject(new Error("SVG image load failed")); };
      img.src = svgUrl;
    });
  };

  const copyQrImage = async () => {
    setBusy("copy-img");
    try {
      const blob = await renderPng({ posterLayout: false });
      if (!navigator.clipboard?.write) throw new Error("no-clipboard-api");
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success("QR image copied · paste into Instagram / Twitter / Discord");
    } catch (e) {
      // Fallback: download it — better than silent failure
      try {
        const blob = await renderPng({ posterLayout: false });
        triggerDownload(blob, `neuraldeck-${user.handle}-qr.png`);
        toast.info("Clipboard blocked · saved as PNG instead");
      } catch (e2) {
        toast.error("Couldn't build QR image");
      }
    } finally { setBusy(null); }
  };

  const downloadPoster = async () => {
    setBusy("download");
    try {
      const blob = await renderPng({ posterLayout: true });
      triggerDownload(blob, `neuraldeck-${user.handle}-tip-poster.png`);
      toast.success("Booth poster saved · print at A4");
    } catch (e) {
      toast.error("Couldn't build poster");
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-[#00F0FF]/30 bg-gradient-to-br from-[#0B0E14] to-[#121620] p-6" data-testid="wallet-tip-jar-poster">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2 flex items-center gap-2">
            <Radio className="w-3 h-3"/> // your tip jar
          </div>
          <div className="font-display font-black text-2xl sm:text-3xl">Booth poster</div>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-md">
            Anyone with a phone camera can tip you — no app, no signup. Print
            the poster and stick it on your booth, or paste the QR image
            straight into your stories.
          </p>

          <div className="mt-4 rounded-lg border border-[#1E2638] bg-[#0B0E14] p-3 font-mono text-xs text-[#00F0FF] break-all" data-testid="wallet-tip-url">
            {tipUrl}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={copyLink}
              disabled={!!busy}
              data-testid="wallet-copy-tip-link"
              className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-black uppercase tracking-widest text-xs"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5"/> {busy === "copy-link" ? "Copying…" : "Copy link"}
            </Button>
            <Button
              onClick={copyQrImage}
              disabled={!!busy}
              variant="outline"
              data-testid="wallet-copy-tip-qr-image"
              className="border-[#00F0FF]/40 hover:border-[#00F0FF] text-[#00F0FF] bg-transparent font-black uppercase tracking-widest text-xs"
            >
              <ImageIcon className="w-3.5 h-3.5 mr-1.5"/> {busy === "copy-img" ? "Building…" : "Copy QR image"}
            </Button>
            <Button
              onClick={downloadPoster}
              disabled={!!busy}
              variant="outline"
              data-testid="wallet-download-tip-poster"
              className="border-[#FF2A6D]/40 hover:border-[#FF2A6D] text-[#FF2A6D] bg-transparent font-black uppercase tracking-widest text-xs"
            >
              <Download className="w-3.5 h-3.5 mr-1.5"/> {busy === "download" ? "Rendering…" : "Download poster (A4)"}
            </Button>
          </div>
        </div>

        {/* Live QR preview — this is the SVG we serialize + rasterize */}
        <div ref={svgWrapRef} className="p-3 rounded-xl bg-white flex flex-col items-center gap-2" data-testid="wallet-tip-qr-wrap">
          <QRCodeSVG value={tipUrl} size={160} level="M" bgColor="#ffffff" fgColor="#0B0E14"/>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B0E14] font-black">
            @{user.handle}
          </div>
        </div>
      </div>
    </div>
  );
}

function triggerDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
